import { UpstreamRequestError, isUpstreamRequestError } from '@koala/sdk/v4';
import {
  NonJsonResponseError,
  isNonJsonResponseError,
  isNetworkError,
} from '@/utils/networkErrors';
import {
  reportNonJsonError,
  reportUpstreamError,
  reportNetworkError,
  type NonJsonErrorContext,
} from '@/utils/reportNonJsonError';
import {
  retryPromiseWithBackoff,
  retryWithBackoff,
  type RetryPromiseOptions,
  type RetryWithBackoffOptions,
} from '@/utils/requests';

/**
 * Detects the `SyntaxError: Unexpected token '<'...` family thrown when
 * `Response.json()` (via wretch's `.json()` or otherwise) tries to parse an
 * HTML body — typically a gateway/CDN error page returned in place of JSON.
 */
const isHtmlInJsonParseError = (error: unknown): error is SyntaxError =>
  error instanceof SyntaxError && /Unexpected token .?</.test(error.message);

/**
 * A binary (`Accept: application/zlib`) read — the compressed `getMenu` path — that
 * receives an HTML error page or a truncated body fails to inflate rather than
 * `JSON.parse`, so it can't produce the `SyntaxError` signal above. `@koala/sdk`
 * surfaces that as a `DecompressionError`; recognize it by name (no need to import
 * the SDK class) so the guard covers this path too. TEAM-13878.
 */
const isDecompressionError = (error: unknown): error is Error =>
  error instanceof Error && error.name === 'DecompressionError';

export interface WithJsonGuardOptions {
  /**
   * Whether to emit the structured Sentry event when a non-JSON response is
   * caught. Defaults to `true`. Set to `false` when the guard runs inside a
   * retry that reports once on final failure (see {@link retryGuardedRead} /
   * {@link retryGuardedReadSaga}) so transient blips that recover on a later
   * attempt don't emit an `error` event per attempt.
   */
  report?: boolean;
}

/**
 * Wrap a coo-api call so that an HTML-instead-of-JSON response is converted
 * into a structured {@link NonJsonResponseError} and (by default) reported to
 * Sentry with URL + status context. Other failures pass through unchanged.
 *
 * Safe to use from sagas (`yield call(withJsonGuard, fn, ctx)`) and from
 * React-Query `queryFn`s. When composing inside one of this app's retry
 * helpers, prefer {@link retryGuardedRead} / {@link retryGuardedReadSaga} —
 * they suppress per-attempt reporting here and report once on final failure.
 */
export async function withJsonGuard<T>(
  fn: () => Promise<T>,
  ctx: NonJsonErrorContext = {},
  { report = true }: WithJsonGuardOptions = {},
): Promise<T> {
  try {
    return await fn();
  } catch (error) {
    if (isNonJsonResponseError(error)) {
      if (report) reportNonJsonError(error, ctx);
      throw error;
    }
    if (isHtmlInJsonParseError(error) || isDecompressionError(error)) {
      const wrapped = new NonJsonResponseError({
        url: ctx.urlHint ?? 'unknown',
        cause: error,
      });
      if (report) reportNonJsonError(wrapped, ctx);
      throw wrapped;
    }
    // Already enriched — pass through without re-wrapping.
    if (isUpstreamRequestError(error)) {
      throw error;
    }

    // Plain HTTP 5xx — enrich with upstream context so Sentry groups by real cause instead of a
    // blank signature. 4xx errors are intentional API responses with structured error data; wrapping
    // them would lose the callable `.text()` that `prepareErrorMessage` relies on for classification.
    const wretchErr = error as {
      status?: number;
      text?: string;
      response?: { headers?: { get?: (name: string) => string | null } };
    };
    const status = wretchErr?.status;
    const isServerError = typeof status === 'number' && status >= 500;

    if (isServerError) {
      const wrapped = new UpstreamRequestError({
        url: ctx.urlHint ?? 'unknown',
        source: ctx.source ?? 'api',
        httpStatus: status,
        bodyExcerpt: typeof wretchErr.text === 'string' ? wretchErr.text : undefined,
        requestId: wretchErr.response?.headers?.get?.('x-request-id') ?? undefined,
        correlationId: wretchErr.response?.headers?.get?.('x-correlation-id') ?? null,
        cause: error,
      });
      if (report) reportUpstreamError(wrapped, ctx);
      throw wrapped;
    }

    // A browser transport failure with no HTTP status (Safari "Load failed", Chrome "Failed to
    // fetch", undici "fetch failed") never reached coo-api, so it is NOT an upstream outage.
    // Report it under its own warning-level `network_failure` class and rethrow the ORIGINAL error
    // unchanged, so the retry predicates and the connection-recovery UI still see a network error.
    // Previously this shared the 5xx branch and surfaced as an `UpstreamRequestError`, which counted
    // flaky diner connections as coo-api /menus 5xx outages. (TEAM-15165)
    if (isNetworkError(error)) {
      if (report) reportNetworkError(error, ctx);
      throw error;
    }

    throw error;
  }
}

/**
 * Retry `onFinalFailure` handler that reports a guarded failure to Sentry exactly once — after the
 * retry budget is exhausted — tagging the attempt count. A non-JSON or 5xx upstream error reports at
 * `error` level; a browser transport failure reports at `warning` level under its own
 * `network_failure` class (TEAM-15165), so a diner's flaky connection is not counted as a coo-api
 * outage. Any other error (e.g. a 4xx or a local guard throw) is not reported here. Also used by the
 * React-Query cache for guarded `queryFn`s, which retry through React-Query rather than a helper
 * here (see `utils/queryCacheReporting.ts`).
 */
export const reportOnFinalFailure =
  (ctx: NonJsonErrorContext) =>
  (error: unknown, attempts: number): void => {
    if (isNonJsonResponseError(error)) {
      reportNonJsonError(error, { ...ctx, attempt: attempts });
    } else if (isUpstreamRequestError(error)) {
      reportUpstreamError(error, { ...ctx, attempt: attempts });
    } else if (isNetworkError(error)) {
      reportNetworkError(error, { ...ctx, attempt: attempts });
    }
  };

/**
 * Retry an idempotent read that may return a non-JSON (HTML) body: guards the
 * response so an HTML error page becomes a retryable {@link NonJsonResponseError},
 * and reports to Sentry once on final failure instead of once per attempt.
 * Promise flavor — use from page `getServerSideProps` and React-Query `queryFn`s.
 */
export function retryGuardedRead<T>(
  fn: () => Promise<T>,
  ctx: NonJsonErrorContext,
  options: RetryPromiseOptions = {},
): Promise<T> {
  return retryPromiseWithBackoff(() => withJsonGuard(fn, ctx, { report: false }), {
    ...options,
    onFinalFailure: reportOnFinalFailure(ctx),
  });
}

/**
 * Saga flavor of {@link retryGuardedRead} — `yield*` it from a saga so the
 * backoff `delay`s stay cancellable by redux-saga (`takeLatest`, etc.).
 */
export function* retryGuardedReadSaga<T>(
  fn: () => Promise<T>,
  ctx: NonJsonErrorContext,
  options: RetryWithBackoffOptions = {},
): Generator<unknown, T> {
  return yield* retryWithBackoff(() => withJsonGuard(fn, ctx, { report: false }), {
    ...options,
    onFinalFailure: reportOnFinalFailure(ctx),
  });
}
